import { $api } from '@/services/api';
import store from '@/store';

const normalizeStatus = (status) =>
  status ? status.toString().trim().toLowerCase() : '';

const fetchAllByQueryString = async (fetchPage, baseQuery) => {
  const rows = [];
  let skip = 0;
  let total = null;

  while (total === null || rows.length < total) {
    const response = await fetchPage(
      `${baseQuery}&$skip=${skip}&$limit=${Number.MAX_SAFE_INTEGER}`
    );
    const pageRows = response?.data || [];

    rows.push(...pageRows);
    total = Number(response?.total ?? rows.length);

    if (!pageRows.length) break;
    skip += pageRows.length;
  }

  return rows;
};

const state = {
  totalPoSubmit: {
    all: 0,
    inProcess: 0,
    waitingForApproval: 0,
  },
  totalPoApproval: {
    all: 0,
    inProcess: 0,
    history: 0,
  },
  totalCancelPoApproval: 0,
  cancelPoCounters: {
    allPo: 0,
    waitingForApproval: 0,
    rejected: 0,
    approved: 0,
  },
};

const getters = {
  totalPoSubmit: (state) => state.totalPoSubmit,
  totalPoApproval: (state) => state.totalPoApproval,
  totalCancelPoApproval: (state) => state.totalCancelPoApproval,
  cancelPoCounters: (state) => state.cancelPoCounters,
};

const mutations = {
  async setAllPoSubmit(state, total) {
    state.totalPoSubmit.all = total;
  },
  async setInProcessPoSubmit(state, total) {
    state.totalPoSubmit.inProcess = total;
  },
  async setWaitingForApprovalPoSubmit(state, total) {
    state.totalPoSubmit.waitingForApproval = total;
  },
  async setAllPoApproval(state, total) {
    state.totalPoApproval.all = total;
  },
  async setInProcessPoApproval(state, total) {
    state.totalPoApproval.inProcess = total;
  },
  async setTotalCancelPoApproval(state, total) {
    state.totalCancelPoApproval = total;
  },
  async setCancelPoCounters(state, counters) {
    state.cancelPoCounters.allPo = counters.allPo;
    state.cancelPoCounters.waitingForApproval = counters.waitingForApproval;
    state.cancelPoCounters.rejected = counters.rejected;
    state.cancelPoCounters.approved = counters.approved;
  },
};

const actions = {
  async getTotalPoSubmit({ commit }) {
    const inProcessPo = await $api.v_pos.getPosWithQueryString(
      store.getters.isSpecialUser
        ? `?status[$in][0]=Waiting for Submit Approval&status[$in][1]=Rejected&is_active=true`
        : `?status[$in][0]=Waiting for Submit Approval&status[$in][1]=Rejected&buyer_employee_code=${store.getters.user.empCodeHC}&is_active=true`
    );
    const waitingForApprovalPo = await $api.v_pos.getPosWithQueryString(
      store.getters.isSpecialUser
        ? `?status=Waiting for Approval&is_active=true`
        : `?status=Waiting for Approval&buyer_employee_code=${store.getters.user.empCodeHC}&is_active=true`
    );
    
    // Fetch Cancel PO count
    const cancelPoQuery = store.getters.isSpecialUser
      ? `?cancel_status=Waiting for Procurement Approval&is_active=true`
      : `?cancel_status=Waiting for Procurement Approval&buyer_employee_code=${store.getters.user.empCodeHC}&is_active=true`;
    const cancelPo = await $api.v_pos.getPosWithQueryString(cancelPoQuery);
    
    const inProcessCount = inProcessPo?.total || 0;
    const waitingCount = waitingForApprovalPo?.total || 0;
    const cancelPoCount = cancelPo?.total || 0;

    commit('setAllPoSubmit', inProcessCount + waitingCount + cancelPoCount);
    commit('setInProcessPoSubmit', inProcessCount);
    commit('setWaitingForApprovalPoSubmit', waitingCount);
  },
  async getTotalPoApproval({ commit }) {
    const approvalRows = await fetchAllByQueryString(
      $api.v_po_approval_items.getByQueryString.bind(
        $api.v_po_approval_items
      ),
      `?status=Waiting&is_active=true&aduser=${store.getters.user.userName}`
    );
    if (!approvalRows.length) {
      commit('setInProcessPoApproval', 0);
      return;
    }

    const idSet = new Set();
    const numberSet = new Set();
    approvalRows.forEach((row) => {
      if (row.po_id) {
        idSet.add(row.po_id);
      } else if (row.po_number) {
        numberSet.add(row.po_number);
      }
    });

    const fetchPoMap = async (field, values) => {
      const map = new Map();
      if (!values.length) return map;
      const chunkSize = 20;
      const entries = Array.from(values);
      for (let i = 0; i < entries.length; i += chunkSize) {
        const chunk = entries.slice(i, i + chunkSize);
        const query = chunk
          .map(
            (value, index) =>
              `${field}[$in][${index}]=${encodeURIComponent(value)}`
          )
          .join('&');
        const response = await $api.po.getPosWithQueryString(
          `?${query}&$limit=${chunk.length}`
        );
        (response?.data || []).forEach((po) => {
          const key = field === 'id' ? po.id : po.po_number;
          map.set(key, po);
        });
      }
      return map;
    };

    const poById = await fetchPoMap('id', Array.from(idSet));
    const poByNumber = await fetchPoMap('po_number', Array.from(numberSet));

    const filtered = approvalRows.filter((row) => {
      const resolvedPo = row.po_id
        ? poById.get(row.po_id)
        : poByNumber.get(row.po_number);
      const status = resolvedPo?.cancel_status ?? row.cancel_status;
      const normalized = normalizeStatus(status);
      return !normalized || normalized === 'rejected';
    });

    commit('setInProcessPoApproval', filtered.length);
  },
  async getTotalCancelPoApproval({ commit }) {
    const response = await $api.m_po_cancel_approval_items.get({
      aduser: store.getters.user.userName,
      status: 'Waiting',
      is_active: true,
    });
    const total =
      typeof response?.total === 'number'
        ? response.total
        : response?.data?.length || 0;
    commit('setTotalCancelPoApproval', total);
  },
  async getCancelPoCounters({ commit }) {
    const baseParams = ['is_active=true'];
    if (!store.getters.isSpecialUser) {
      baseParams.push(
        `requested_by_user=${encodeURIComponent(store.getters.user.userName)}`
      );
    }

    const buildQuery = (extra = []) =>
      `?${[...baseParams, ...extra].join('&')}`;

    const fetchTotal = async (extra = []) => {
      const response = await $api.po.getPosWithQueryString(
        buildQuery([...extra, '$limit=0'])
      );

      return typeof response?.total === 'number' ? response.total : 0;
    };

    try {
      const waitingForApproval = await fetchTotal([
        'cancel_status[$in][0]=Waiting for Approval',
        'cancel_status[$in][1]=Waiting for Procurement Approval',
      ]);
      const rejected = await fetchTotal(['cancel_status=Rejected']);
      const approved = await fetchTotal(['cancel_status=Approved']);

      commit('setCancelPoCounters', {
        allPo: 0,
        waitingForApproval,
        rejected,
        approved,
      });
    } catch (error) {
      const response = await $api.po.getPosWithQueryString(
        buildQuery(['$limit=5000'])
      );
      const rows = response?.data || [];

      const normalizeStatus = (status) => {
        if (!status) return 'null';
        const trimmed = status.trim();

        return trimmed ? trimmed.toLowerCase() : 'null';
      };

      const counters = rows.reduce(
        (acc, row) => {
          const status = normalizeStatus(row.cancel_status);
          if (status === 'null') acc.allPo += 1;
          else if (
            status === 'waiting for approval' ||
            status === 'waiting for procurement approval'
          )
            acc.waitingForApproval += 1;
          else if (status === 'rejected') acc.rejected += 1;
          else if (status === 'approved') acc.approved += 1;
          
          return acc;
        },
        { allPo: 0, waitingForApproval: 0, rejected: 0, approved: 0 }
      );

      commit('setCancelPoCounters', counters);
    }
  },
};

export default { state, getters, mutations, actions };
